import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Briefcase, Filter, Loader2, Sparkles } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { getAllPosts } from "reducer/postsSlice";
import { resetOtherUserProfile } from "reducer/profileSlice";
import { getProjects } from "reducer/projectsSlice";
import { API } from "service/Interceptor";
import { suggestedCreativesAPI } from "service/apiUrl";
import { decodeBriefPlainText, formatPersonName, serverFiles } from "utils/utils";
import {
  isCreatorBriefPost,
  mapPostToOpenBrief,
} from "utils/creatorBriefs";
import LeftSidebar from "components/app/LeftSidebar";
import FeedComposer from "components/feed/FeedComposer";
import FeedPostCard from "components/feed/FeedPostCard";
import OpenBriefCard from "components/feed/OpenBriefCard";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const FILTERS = [
  { label: "All", value: "ALL" },
  { label: "Network", value: "NETWORK" },
  { label: "Advice", value: "ADVICE" },
  { label: "Thoughts", value: "THOUGHTS" },
  { label: "Briefs", value: "BRIEFS" },
];

/** Legacy composers stored tags as "0"/"1"/"2"; match both forms. */
const TAG_QUERY = {
  NETWORK: ["NETWORK", "0", "network", "Network"],
  ADVICE: ["ADVICE", "1", "advice", "Advice"],
  THOUGHTS: ["THOUGHTS", "2", "thoughts", "Thoughts"],
  BRIEFS: ["BRIEFS"],
};

const buildPostQuery = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v != null && v !== "") searchParams.append(key, String(v));
      });
    } else {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

const fetchBriefPosts = async (perPage = 50) => {
  const qs = buildPostQuery({
    pageNo: 1,
    perPage,
    sortBy: "latest",
    tags: "BRIEFS",
  });
  const res = await API.get(`api/post?${qs}`);
  return (res?.data?.data?.results || [])
    .filter(isCreatorBriefPost)
    .map(mapPostToOpenBrief)
    .filter(Boolean);
};

const SidebarCard = ({ children }) => (
  <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
    {children}
  </div>
);

const Avatar = ({ src, name, size = 40 }) =>
  src ? (
    <img
      src={src}
      alt={name}
      style={{ height: size, width: size }}
      className="rounded-full object-cover shrink-0"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = NO_IMAGE;
      }}
    />
  ) : (
    <div
      style={{ height: size, width: size }}
      className="avatar-fallback rounded-full shrink-0 flex items-center justify-center font-semibold text-sm"
    >
      {(name || "Q")
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()}
    </div>
  );

const formatBudget = (n) => {
  if (n == null || n === "") return null;
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num >= 100000)
    return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)}L`;
  if (num >= 1000) return `₹${Math.round(num / 1000)}k`;
  return `₹${num}`;
};

const SocialFeed = () => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { posts } = useSelector((state) => state.posts);
  const { projects } = useSelector((state) => state.projects);
  const { userProfile, isProfileLoading } = useSelector(
    (state) => state.userProfile
  );

  const searchQ = new URLSearchParams(search).get("q")?.toLowerCase() || "";
  const [tag, setTag] = useState("ALL");
  const [isLoading, setIsLoading] = useState(false);
  const [isPaging, setIsPaging] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [creatorBriefs, setCreatorBriefs] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const itemsPerPage = 20;
  const isBriefs = tag === "BRIEFS";

  const fetchPosts = async ({ reset = false } = {}) => {
    if (isBriefs) {
      setIsLoading(true);
      try {
        await dispatch(
          getProjects({
            pageNo: 1,
            perPage: 40,
            sortBy: "LATEST",
            search: "_",
            preference: false,
          })
        );
        const mapped = await fetchBriefPosts(50);
        setCreatorBriefs(mapped);
      } catch {
        setCreatorBriefs([]);
      }
      setHasMore(false);
      setIsLoading(false);
      return;
    }

    const currentPage = reset ? 1 : page;
    if (reset) {
      setIsLoading(true);
      setHasMore(true);
    } else {
      if (!hasMore || isPaging || isLoading) return;
      setIsPaging(true);
    }

    const params = {
      pageNo: currentPage,
      perPage: itemsPerPage,
      sortBy: "latest",
    };
    if (tag !== "ALL") {
      params.tags = TAG_QUERY[tag] || tag;
    }

    try {
      const res = await dispatch(getAllPosts(params));
      const batch = res?.payload?.posts || [];
      if (batch.length < itemsPerPage) setHasMore(false);
      else setHasMore(true);
      if (!reset) setPage((p) => p + 1);
      else setPage(2);
    } finally {
      setIsLoading(false);
      setIsPaging(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    dispatch(resetOtherUserProfile());
  }, [pathname, dispatch]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchPosts({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  // If the first page(s) don't fill the viewport, InfiniteScroll never fires —
  // keep loading until the feed is scrollable or exhausted.
  useEffect(() => {
    if (isBriefs || isLoading || isPaging || !hasMore) return;
    const short =
      document.documentElement.scrollHeight <=
      window.innerHeight + 160;
    if (short && (posts || []).length > 0) {
      fetchPosts({ reset: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, hasMore, isLoading, isPaging, isBriefs, tag]);

  useEffect(() => {
    dispatch(
      getProjects({
        pageNo: 1,
        perPage: 5,
        sortBy: "LATEST",
        search: "_",
        preference: false,
      })
    );
    (async () => {
      try {
        const mapped = await fetchBriefPosts(10);
        setCreatorBriefs(mapped);
      } catch {
        /* sidebar can stay brand-only */
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(suggestedCreativesAPI);
        const rows = res?.data?.data || [];
        if (cancelled) return;
        setSuggested(
          (Array.isArray(rows) ? rows : [])
            .map((row) => {
              const name = formatPersonName(row?.firstName, row?.lastName);
              if (!row?.userAliasId || !name) return null;
              return {
                id: row.userAliasId,
                name,
                role: row.matchLabel || "Creative",
                avatar: row.profileUrl ? serverFiles(row.profileUrl) : null,
              };
            })
            .filter(Boolean)
            .slice(0, 3)
        );
      } catch {
        if (!cancelled) setSuggested([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openBriefs = useMemo(() => {
    const seen = new Set();
    const merged = [];
    for (const p of [...creatorBriefs, ...(projects || [])]) {
      const key = String(p?.jobId || "");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(p);
    }
    return merged
      .sort(
        (a, b) =>
          new Date(b?.createdAt || 0).getTime() -
          new Date(a?.createdAt || 0).getTime()
      )
      .slice(0, 2);
  }, [creatorBriefs, projects]);

  const visiblePosts = useMemo(() => {
    return (posts || []).filter((p) => {
      if (!searchQ) return true;
      const hay = [
        p?.content,
        ...(p?.tags || []),
        p?.postOwner?.firstName,
        p?.postOwner?.lastName,
        p?.postOwner?.companyName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(searchQ);
    });
  }, [posts, searchQ]);

  const visibleBriefs = useMemo(() => {
    const seen = new Set();
    const merged = [];
    for (const p of [...creatorBriefs, ...(projects || [])]) {
      const key = String(p?.jobId || "");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(p);
    }
    return merged.filter((p) => {
      if (!searchQ) return true;
      const hay = [
        p?.jobTitle,
        p?.description,
        p?.companyJobRespDto?.name,
        p?.company?.name,
        p?.cityName,
        ...(p?.skillsRequired || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(searchQ);
    });
  }, [projects, creatorBriefs, searchQ]);

  if (isProfileLoading && !userProfile) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-500 gap-2 font-sans-body">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 font-sans-body">
      <aside className="hidden lg:flex lg:col-span-3 flex-col gap-5 self-start sticky top-24">
        <LeftSidebar
          filters={FILTERS}
          currentFilter={tag}
          onFilterChange={setTag}
          filterTitle="Filter feed"
        />
      </aside>

      <section className="lg:col-span-6 space-y-4 sm:space-y-5 min-w-0">
        <FeedComposer
          onPosted={() => {
            dispatch(
              getProjects({
                pageNo: 1,
                perPage: 5,
                sortBy: "LATEST",
                search: "_",
                preference: false,
              })
            );
          }}
        />

        <div className="lg:hidden -mx-3 sm:mx-0 px-3 sm:px-0">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-zinc-500 font-semibold shrink-0 pr-1">
              <Filter size={12} className="text-[#8b83ff]" /> Filter
            </span>
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setTag(f.value)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium whitespace-nowrap transition-colors shrink-0 ${
                  tag === f.value
                    ? "border-[#6C63F5] bg-[#6C63F5]/15 text-zinc-50"
                    : "border-zinc-800 bg-zinc-900/50 text-zinc-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile open briefs strip */}
        {!isBriefs && openBriefs.length > 0 && (
          <div className="lg:hidden -mx-3 sm:mx-0">
            <div className="px-3 sm:px-0 flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 font-semibold">
                <Briefcase size={12} className="text-[#8b83ff]" /> Open briefs
              </div>
              <Link
                to="/projects"
                className="text-xs text-[#8b83ff] font-medium"
              >
                See all
              </Link>
            </div>
            <div className="flex gap-3 overflow-x-auto px-3 sm:px-0 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {openBriefs.map((b) => (
                <Link
                  key={b.jobId}
                  to="/projects"
                  className="shrink-0 w-[15.5rem] rounded-xl border border-zinc-800 bg-zinc-900/50 p-3.5"
                >
                  <div className="text-xs text-[#8b83ff] font-medium truncate">
                    {b?.companyJobRespDto?.name || b?.company?.name || "Brand"}
                  </div>
                  <div className="mt-1 text-sm text-zinc-100 leading-snug line-clamp-2 min-h-[2.5rem]">
                    {decodeBriefPlainText(b?.jobTitle)}
                  </div>
                  <div className="mt-2 text-xs text-zinc-500 truncate">
                    {(Array.isArray(b?.skillsRequired) &&
                      b.skillsRequired[0]) ||
                      "Creative"}
                    {formatBudget(b?.projectBudget)
                      ? ` · ${formatBudget(b?.projectBudget)}`
                      : ""}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading feed…
          </div>
        ) : isBriefs ? (
          visibleBriefs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-800 py-16 text-center text-zinc-500">
              No briefs to show.
            </div>
          ) : (
            <div className="space-y-5">
              {visibleBriefs.map((p) => (
                <OpenBriefCard key={p.jobId} project={p} />
              ))}
            </div>
          )
        ) : visiblePosts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-zinc-800 py-16 text-center text-zinc-500">
            {searchQ
              ? `No posts match "${searchQ}".`
              : tag === "ALL"
                ? "No posts yet — be the first to share something."
                : `No ${tag.toLowerCase()} to show.`}
          </div>
        ) : (
          <InfiniteScroll
            dataLength={visiblePosts.length}
            next={() => fetchPosts({ reset: false })}
            hasMore={hasMore && !isPaging}
            loader={
              <div className="py-6 text-center text-zinc-500 flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading more…
              </div>
            }
            className="space-y-5"
          >
            {visiblePosts.map((post) => (
              <FeedPostCard key={post.id} post={post} />
            ))}
          </InfiniteScroll>
        )}
      </section>

      <aside className="hidden lg:block lg:col-span-3 space-y-5 self-start sticky top-24">
        <SidebarCard>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-4">
            <Sparkles size={14} className="text-[#8b83ff]" /> Creatives to
            follow
          </div>
          <div className="space-y-4">
            {suggested.length === 0 ? (
              <div className="text-sm text-zinc-500">
                No creatives match your industries yet.
              </div>
            ) : (
              suggested.map((s) => (
                <div key={s.id} className="flex items-center gap-3">
                  <Link to={`/user-profile/${s.id}`} className="shrink-0">
                    <Avatar src={s.avatar} name={s.name} size={40} />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/user-profile/${s.id}`}
                      className="block text-sm font-medium text-zinc-100 truncate hover:text-[#c9c5ff]"
                    >
                      {s.name}
                    </Link>
                    <div className="text-xs text-zinc-500 truncate">
                      {s.role}
                    </div>
                  </div>
                  <Link
                    to={`/user-profile/${s.id}`}
                    className="rounded-full border border-zinc-700 px-3 py-1 text-xs font-medium text-zinc-200 hover:border-[#6C63F5] hover:text-white transition-colors"
                  >
                    View
                  </Link>
                </div>
              ))
            )}
          </div>
        </SidebarCard>

        <SidebarCard>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-4">
            <Briefcase size={14} className="text-[#8b83ff]" /> Open briefs
          </div>
          <div className="space-y-4">
            {openBriefs.length === 0 ? (
              <div className="text-sm text-zinc-500">No open briefs.</div>
            ) : (
              openBriefs.map((b) => (
                <Link
                  key={b.jobId}
                  to="/projects"
                  className="block rounded-xl border border-zinc-800 bg-zinc-900/50 p-3.5 hover:border-zinc-700 transition-colors"
                >
                  <div className="text-xs text-[#8b83ff] font-medium">
                    {b?.companyJobRespDto?.name || b?.company?.name || "Brand"}
                  </div>
                  <div className="mt-1 text-sm text-zinc-100 leading-snug line-clamp-2">
                    {decodeBriefPlainText(b?.jobTitle)}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs text-zinc-500">
                    <span className="truncate">
                      {(Array.isArray(b?.skillsRequired) &&
                        b.skillsRequired[0]) ||
                        "Creative"}
                    </span>
                    <span className="text-zinc-300 shrink-0">
                      {formatBudget(b?.projectBudget) || ""}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </SidebarCard>
      </aside>
    </div>
  );
};

export default SocialFeed;
